import React, { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

// Interfaces
interface StackJumperProps {
  containerWidth: number;
  containerHeight: number;
  onExit: () => void;
}

interface Tool {
  id: string;
  name: string;
  logo_url: string;
}

interface Platform {
  x: number;
  y: number;
  width: number;
  height: number;
  toolId: string;
}

interface UnicornState {
  x: number;
  y: number;
  width: number;
  height: number;
  dx: number;
  dy: number;
  onGround: boolean;
  isJumping: boolean; // Used for enabling double jump logic
  targetDx: number;
  maxDx: number;
  horizontalAcceleration: number;
}

interface GameState {
  unicorn: UnicornState;
  platforms: Platform[];
  platformWidth: number;
  platformHeight: number;
  platformCount: number;
  platformVerticalSpacing: number;
  maxHorizontalPlatformOffset: number;
  gravity: number;
  baseGravity: number;
  jumpForce: number;
  baseJumpForce: number;
  minUnicornY: number; // Y position threshold to trigger camera scroll
  baseCameraSpeed: number;
  cameraSpeed: number;
  cameraSpeedAcceleration: number;
  scoreBenchmarkIncrement: number;
  lastScoreBenchmark: number;
  speedBoostPerBenchmark: number;
  gravityReductionPerBenchmark: number;
  jumpForceIncreasePerBenchmark: number;
  score: number;
  totalScroll: number;
  gameStarted: boolean;
  gameOver: boolean;
  isTouchDevice: boolean;
  maxSpeedCap: number;
  airJumpEnabled: boolean;
  airJumpThreshold: number;
  gameOverSoundPlayed: boolean;
}

// Constants
const UNICORN_WIDTH = 40;
const UNICORN_HEIGHT = 40;
const PLATFORM_WIDTH = 50;
const PLATFORM_HEIGHT = 50;
const CLOUD_SVG_WIDTH = 60; // Corresponds to viewBox width
const CLOUD_SVG_HEIGHT = 30; // Corresponds to viewBox height

const StackJumper: React.FC<StackJumperProps> = ({ containerWidth, containerHeight, onExit }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scoreRef = useRef<HTMLDivElement>(null);
  const startMessageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number | undefined>(undefined);
  
  // Asset loading state
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [tools, setTools] = useState<Tool[]>([]);
  const toolImages = useRef<Map<string, HTMLImageElement>>(new Map());
  const unicornSprites = useRef<{
    standing: HTMLImageElement | null;
    sitting: HTMLImageElement | null;
  }>({ standing: null, sitting: null });
  const cloudSprites = useRef<HTMLImageElement[]>([]);
  const cloudPositions = useRef<{ x: number; y: number; speed: number }[]>([]);
  
  // Sound effects
  const soundEffects = useRef<{
    jump: AudioBuffer | null;
    milestone: AudioBuffer | null;
    gameOver: AudioBuffer | null;
    audioContext: AudioContext | null;
  }>({ jump: null, milestone: null, gameOver: null, audioContext: null });

  // Game state consolidated into a single ref
  const gameState = useRef<GameState>({
    unicorn: {
      x: 0, y: 0, width: UNICORN_WIDTH, height: UNICORN_HEIGHT,
      dx: 0, dy: 0, onGround: false, isJumping: false, targetDx: 0,
      maxDx: 3.8, horizontalAcceleration: 0.25,
    },
    platforms: [],
    platformWidth: PLATFORM_WIDTH, platformHeight: PLATFORM_HEIGHT,
    platformCount: 8, platformVerticalSpacing: 115,
    maxHorizontalPlatformOffset: 180,
    gravity: 0.45, baseGravity: 0.45,
    jumpForce: -12.0, baseJumpForce: -12.0,
    minUnicornY: containerHeight / 3.5,
    baseCameraSpeed: 0.15, cameraSpeed: 0.15, cameraSpeedAcceleration: 0.00005,
    scoreBenchmarkIncrement: 100, lastScoreBenchmark: 0,
    speedBoostPerBenchmark: 0.3,
    gravityReductionPerBenchmark: 0.002,
    jumpForceIncreasePerBenchmark: 0.05,
    score: 0, totalScroll: 0, gameStarted: false, gameOver: false,
    isTouchDevice: false, maxSpeedCap: 3.5, airJumpEnabled: false,
    airJumpThreshold: 500, gameOverSoundPlayed: false,
  });

  // Helper function to create sounds
  const createSound = (context: AudioContext, duration: number, frequencyFunc: (i: number, len: number) => number, volumeFunc: (i: number, len: number) => number): AudioBuffer => {
    const sampleRate = context.sampleRate;
    const bufferLength = sampleRate * duration;
    const buffer = context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferLength; i++) {
        const freq = frequencyFunc(i, bufferLength);
        const vol = volumeFunc(i, bufferLength);
        data[i] = (Math.sin(i * freq * 2 * Math.PI / sampleRate) > 0 ? 1 : -1) * vol;
    }
    return buffer;
  };

  // Initialize Sound Effects
  useEffect(() => {
    const context = new (window.AudioContext || (window as any).webkitAudioContext)();
    soundEffects.current.audioContext = context;

    soundEffects.current.jump = createSound(context, 0.12, (_i) => 880, (i, len) => 0.7 * (1 - i / len));
    soundEffects.current.milestone = createSound(context, 0.3, (i, len) => (i < len / 2 ? 660 : 440), (i, len) => 0.8 * (1 - (i % (len / 2)) / (len / 2)));
    soundEffects.current.gameOver = createSound(context, 0.7, (i, len) => 220 - (Math.floor(i / (len / 3)) * 40), (i, len) => 0.7 * (1 - (i % (len / 3)) / (len / 3)));

    return () => { context.state !== 'closed' && context.close(); };
  }, []);

  // Play Sound Function
  const playSound = useCallback((soundType: 'jump' | 'milestone' | 'gameOver') => {
    const { audioContext, [soundType]: soundBuffer } = soundEffects.current;
    if (!audioContext || !soundBuffer || audioContext.state === 'closed') return;
    if (audioContext.state === 'suspended') audioContext.resume();

    const source = audioContext.createBufferSource();
    source.buffer = soundBuffer;
    source.connect(audioContext.destination);
    source.start();
  }, []);


  // Load Assets (Tools, Unicorn Sprites, Clouds)
  useEffect(() => {
    let isMounted = true;
    let assetsStillLoading = 3; // Tools, Unicorn Standing, Unicorn Sitting

    const checkAllLoaded = () => {
        assetsStillLoading--;
        if (isMounted && assetsStillLoading === 0) setAssetsLoaded(true);
    };

    // 1. Fetch Tools and Load Logos
    const fetchAndLoadTools = async () => {
      try {
        const { data, error } = await supabase.from('tools').select('id, name, logo_url').not('logo_url', 'is', null);
        if (!isMounted) return;
        if (error) throw error;

        if (data?.length > 0) {
            setTools(data);
            const imageMap = new Map<string, HTMLImageElement>();
            let toolImagesToLoad = data.length;
            if (toolImagesToLoad === 0) { checkAllLoaded(); return; }
            let toolImagesLoaded = 0;
            data.forEach(tool => {
                const img = new Image();
                img.onload = img.onerror = () => {
                    toolImagesLoaded++;
                    if (toolImagesLoaded === toolImagesToLoad && isMounted) {
                        toolImages.current = imageMap;
                        checkAllLoaded();
                    }
                };
                img.src = tool.logo_url;
                imageMap.set(tool.id, img);
            });
        } else { checkAllLoaded(); }
      } catch (error) {
          console.error('Error loading tools:', error);
          if (isMounted) checkAllLoaded();
      }
    };

    // 2. Load Unicorn Sprites
    const loadUnicornSprite = (src: string, type: 'standing' | 'sitting') => {
        const img = new Image();
        img.onload = img.onerror = () => { if (isMounted) checkAllLoaded(); };
        img.src = src;
        unicornSprites.current[type] = img;
    };

    fetchAndLoadTools();
    loadUnicornSprite('https://yamkpcvgqmdyujqofdfg.supabase.co/storage/v1/object/public/assets//unicorn_standing_s.png', 'standing');
    loadUnicornSprite('https://yamkpcvgqmdyujqofdfg.supabase.co/storage/v1/object/public/assets//unicorn_sitting_s.png', 'sitting');

    return () => { isMounted = false; };
  }, []);

  // Initialize Clouds - Triggered AFTER assetsLoaded = true
  const initClouds = useCallback(() => {
    const cloudCount = 5;
    cloudSprites.current = [];
    cloudPositions.current = [];
    for (let i = 0; i < cloudCount; i++) {
      const cloud = new Image();
      // BLOCKY cloud SVG using rectangles
      cloud.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CLOUD_SVG_WIDTH} ${CLOUD_SVG_HEIGHT}" width="${CLOUD_SVG_WIDTH}" height="${CLOUD_SVG_HEIGHT}"><rect x="10" y="15" width="15" height="10" fill="white" opacity="0.8"/><rect x="20" y="10" width="20" height="15" fill="white" opacity="0.8"/><rect x="35" y="15" width="15" height="10" fill="white" opacity="0.8"/></svg>`;
      cloudSprites.current.push(cloud);
      cloudPositions.current.push({
        x: Math.random() * containerWidth,
        y: Math.random() * (containerHeight / 2),
        speed: 0.15 + Math.random() * 0.25
      });
    }
  }, [containerWidth, containerHeight]);

  // Initialize clouds only when assets are loaded
  useEffect(() => {
    if (assetsLoaded) {
      initClouds();
    }
  }, [assetsLoaded, initClouds]);


  // Create Platform Helper
  const createPlatform = useCallback((y: number, referenceX?: number): Platform | null => {
    const { platformWidth, platformHeight, maxHorizontalPlatformOffset } = gameState.current;
    const safeAreaWidth = containerWidth - platformWidth;
    referenceX = referenceX ?? containerWidth / 2;
    const minX = Math.max(0, referenceX - maxHorizontalPlatformOffset);
    const maxX = Math.min(safeAreaWidth, referenceX + maxHorizontalPlatformOffset);
    const spawnRangeWidth = Math.max(10, maxX - minX);
    const safeX = Math.max(0, Math.min(safeAreaWidth, minX + Math.random() * spawnRangeWidth));
    const toolId = tools.length > 0 ? tools[Math.floor(Math.random() * tools.length)].id : '';
    return { x: safeX, y, width: platformWidth, height: platformHeight, toolId };
  }, [containerWidth, tools]);


  // Reset Game State
  const resetGame = useCallback(() => {
    const state = gameState.current;
    state.minUnicornY = containerHeight / 3.5;
    state.platforms = [];
    state.totalScroll = 0;
    state.cameraSpeed = state.baseCameraSpeed;
    state.lastScoreBenchmark = 0;
    state.gravity = state.baseGravity;
    state.jumpForce = state.baseJumpForce;
    state.airJumpEnabled = false;
    state.score = 0;
    state.gameStarted = false;
    state.gameOver = false;
    state.gameOverSoundPlayed = false;

    const startPlatform = createPlatform(containerHeight - 100, containerWidth / 2);
    if (startPlatform) {
        state.platforms.push(startPlatform);
        state.unicorn = { // Reset unicorn state fully
            ...state.unicorn,
            x: startPlatform.x + state.platformWidth / 2 - state.unicorn.width / 2,
            y: startPlatform.y - state.unicorn.height,
            dx: 0, dy: 0, targetDx: 0, onGround: true, isJumping: false,
        };

        let currentY = startPlatform.y;
        let lastX = startPlatform.x + state.platformWidth / 2;
        for (let i = 1; i < state.platformCount; i++) {
            currentY -= state.platformVerticalSpacing;
            const newPlatform = createPlatform(currentY, lastX);
            if (newPlatform) {
                state.platforms.push(newPlatform);
                lastX = newPlatform.x + state.platformWidth / 2;
            }
        }
    }

    if (scoreRef.current) scoreRef.current.textContent = `Score: 0`;
    if (startMessageRef.current) startMessageRef.current.style.display = "block";
    if (titleRef.current) titleRef.current.style.display = "block";
    updateStartMessage();

  }, [containerHeight, containerWidth, createPlatform]); // Removed updateStartMessage from deps, it reads state

  // Start Game Action
  const startGame = useCallback(() => {
    if (!gameState.current.gameStarted) {
      gameState.current.gameStarted = true;
      gameState.current.gameOver = false;
      if (startMessageRef.current) startMessageRef.current.style.display = "none";
      if (titleRef.current) titleRef.current.style.display = "none";
    }
  }, []);

  // Jump Action
  const jump = useCallback(() => {
    const { unicorn, airJumpEnabled, score, airJumpThreshold, jumpForce } = gameState.current;
    if (unicorn.onGround) {
      unicorn.dy = jumpForce; unicorn.onGround = false; unicorn.isJumping = true;
      playSound('jump');
    } else if (airJumpEnabled && score >= airJumpThreshold && unicorn.isJumping) {
      unicorn.dy = jumpForce * 0.85; unicorn.isJumping = false;
      playSound('jump');
    }
  }, [playSound]);


  // Update Functions
  const updateUnicornMovement = (state: GameState) => {
    const { unicorn } = state;
    if (unicorn.dx < unicorn.targetDx) unicorn.dx = Math.min(unicorn.targetDx, unicorn.dx + unicorn.horizontalAcceleration);
    else if (unicorn.dx > unicorn.targetDx) unicorn.dx = Math.max(unicorn.targetDx, unicorn.dx - unicorn.horizontalAcceleration);
    unicorn.x += unicorn.dx;
    if (unicorn.x > containerWidth) unicorn.x = -unicorn.width; else if (unicorn.x < -unicorn.width) unicorn.x = containerWidth;
    if (!unicorn.onGround) unicorn.dy += state.gravity;
    unicorn.y += unicorn.dy;
  };

  const handlePlatformCollisions = (state: GameState) => {
    const { unicorn, platforms } = state;
    let landed = false;
    if (unicorn.dy > 0 && !unicorn.onGround) {
        for (const p of platforms) {
            if (unicorn.x < p.x + p.width && unicorn.x + unicorn.width > p.x &&
                unicorn.y + unicorn.height >= p.y && unicorn.y + unicorn.height <= p.y + p.height + 5) {
                unicorn.dy = 0; unicorn.y = p.y - unicorn.height; unicorn.onGround = true; unicorn.isJumping = false; landed = true;
                break;
            }
        }
    }
    if (unicorn.onGround && !landed) { // Check if fell off edge
        let stillSupported = false;
        for (const p of platforms) {
            if (unicorn.x < p.x + p.width && unicorn.x + unicorn.width > p.x && Math.abs(unicorn.y + unicorn.height - p.y) < 1) {
                stillSupported = true; break;
            }
        }
        if (!stillSupported) unicorn.onGround = false;
    }
  };

  const updateCameraAndScroll = (state: GameState) => {
    const { unicorn, platforms, minUnicornY, cameraSpeedAcceleration, maxSpeedCap } = state;
    let scrollAmount = state.cameraSpeed;
    state.cameraSpeed = Math.min(maxSpeedCap, state.cameraSpeed + cameraSpeedAcceleration);
    if (unicorn.y < minUnicornY) scrollAmount += minUnicornY - unicorn.y;
    if (scrollAmount > 0) {
      unicorn.y += scrollAmount;
      platforms.forEach(p => p.y += scrollAmount);
      cloudPositions.current.forEach(pos => {
          pos.y += scrollAmount * 0.5;
          if (pos.y > containerHeight) { pos.y = -CLOUD_SVG_HEIGHT; pos.x = Math.random() * containerWidth; }
      });
      state.totalScroll += scrollAmount;
    }
  };

  const managePlatforms = (state: GameState) => {
    state.platforms = state.platforms.filter(p => p.y < containerHeight + p.height);
    let highestY = state.platforms.length > 0 ? Math.min(...state.platforms.map(p => p.y)) : containerHeight;
    let highestPlatform = state.platforms.find(p => p.y === highestY);
    while (state.platforms.length < state.platformCount) {
        const refX = highestPlatform ? highestPlatform.x + state.platformWidth / 2 : containerWidth / 2;
        const newY = highestY - state.platformVerticalSpacing;
        const newPlatform = createPlatform(newY, refX);
        if (newPlatform) { state.platforms.push(newPlatform); highestPlatform = newPlatform; highestY = newY; }
        else break;
    }
  };

  const updateScoreAndDifficulty = (state: GameState) => {
    const previousScore = state.score;
    state.score = Math.floor(state.totalScroll / 10);
    if (Math.floor(state.score / 100) > Math.floor(previousScore / 100)) playSound('milestone');
    if (state.score >= state.lastScoreBenchmark + state.scoreBenchmarkIncrement) {
        const currentBenchmark = Math.floor(state.score / state.scoreBenchmarkIncrement) * state.scoreBenchmarkIncrement;
        const benchmarksCrossed = Math.max(1, (currentBenchmark - state.lastScoreBenchmark) / state.scoreBenchmarkIncrement);
        state.cameraSpeed += benchmarksCrossed * state.speedBoostPerBenchmark;
        const benchmarkFactor = currentBenchmark / state.scoreBenchmarkIncrement;
        state.gravity = Math.max(state.baseGravity * 0.95, state.baseGravity - (benchmarkFactor * state.gravityReductionPerBenchmark * 0.6));
        state.jumpForce = state.baseJumpForce * (1 - (benchmarkFactor * state.jumpForceIncreasePerBenchmark * 0.08));
        state.lastScoreBenchmark = currentBenchmark;
    }
    if (!state.airJumpEnabled && state.score >= state.airJumpThreshold) state.airJumpEnabled = true;
  };

  const checkGameOverCondition = (state: GameState) => {
    if (state.unicorn.y > containerHeight) {
      state.gameOver = true; state.gameStarted = false;
      if (!state.gameOverSoundPlayed) { playSound('gameOver'); state.gameOverSoundPlayed = true; }
      if (titleRef.current) titleRef.current.style.display = "block";
      if (startMessageRef.current) startMessageRef.current.style.display = "block";
      updateStartMessage();
    }
  };

  // Main Update Logic
  const update = useCallback(() => {
    const state = gameState.current;
    if (!state.gameStarted || state.gameOver) return;
    updateUnicornMovement(state);
    handlePlatformCollisions(state);
    updateCameraAndScroll(state);
    managePlatforms(state);
    updateScoreAndDifficulty(state);
    checkGameOverCondition(state);
  }, [containerHeight, containerWidth, createPlatform, playSound]);


  // Drawing Functions
  const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
    if (w < 2 * r) r = w / 2; if (h < 2 * r) r = h / 2;
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); ctx.fill(); ctx.stroke();
  };
  const drawRetroText = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, color: string) => {
    ctx.fillStyle = color; ctx.font = `${size}px 'Press Start 2P', monospace`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // Apply shadow *only* for this text
    ctx.save(); // Save current context state (including shadows)
    ctx.shadowColor = '#000'; ctx.shadowOffsetX = 2; ctx.shadowOffsetY = 2; ctx.shadowBlur = 0;
    ctx.fillText(text, x, y);
    ctx.restore(); // Restore previous context state (removes shadow settings)
  };

  const drawBackgroundAndClouds = (ctx: CanvasRenderingContext2D) => {
    ctx.fillStyle = '#87CEEB'; ctx.fillRect(0, 0, containerWidth, containerHeight);
    for (let i = 0; i < cloudPositions.current.length; i++) {
        const pos = cloudPositions.current[i]; const cloud = cloudSprites.current[i];
        if (cloud?.complete) {
            ctx.drawImage(cloud, pos.x, pos.y, CLOUD_SVG_WIDTH, CLOUD_SVG_HEIGHT);
            if (gameState.current.gameStarted && !gameState.current.gameOver) {
                pos.x -= pos.speed;
                if (pos.x < -CLOUD_SVG_WIDTH) pos.x = containerWidth;
            }
        }
    }
  };

  const drawGamePlatforms = (ctx: CanvasRenderingContext2D) => {
    ctx.lineWidth = 2; ctx.strokeStyle = '#000';
    for (const p of gameState.current.platforms) {
        const toolImage = p.toolId ? toolImages.current.get(p.toolId) : null;
        if (toolImage?.complete) {
            ctx.fillStyle = '#444'; roundRect(ctx, p.x, p.y, p.width, p.height, 8);
            const imgPadding = 5; const imgSize = Math.min(p.width - imgPadding * 2, p.height - imgPadding * 2);
            ctx.drawImage(toolImage, p.x + (p.width - imgSize) / 2, p.y + (p.height - imgSize) / 2, imgSize, imgSize);
        } else {
            ctx.fillStyle = '#0f0'; roundRect(ctx, p.x, p.y, p.width, p.height, 8);
        }
    }
  };

  const drawPlayerUnicorn = (ctx: CanvasRenderingContext2D) => {
    const { x, y, width, height, onGround } = gameState.current.unicorn;
    const sprite = onGround ? unicornSprites.current.sitting : unicornSprites.current.standing;
    // Glow ONLY if air jump enabled AND unicorn is NOT on the ground
    const glow = gameState.current.airJumpEnabled && !onGround;

    ctx.save(); // Save context state before drawing unicorn (and potentially glow)
    if (glow) {
        ctx.shadowColor = '#f0f'; ctx.shadowBlur = 10;
    }

    if (sprite?.complete) {
        const spriteScale = 1.2; const sw = width * spriteScale; const sh = height * spriteScale;
        const sx = x - (sw - width) / 2; const sy = y - (sh - height) / 2;
        ctx.drawImage(sprite, sx, sy, sw, sh);
    } else {
        ctx.fillStyle = glow ? '#f0f' : '#f46'; // Fallback color
        ctx.fillRect(x, y, width, height);
        ctx.strokeStyle = '#000'; ctx.lineWidth = 1; ctx.strokeRect(x, y, width, height);
    }

    ctx.restore(); // Restore context state (removes shadow settings automatically if they were set)
  };

   const drawUI = (ctx: CanvasRenderingContext2D) => {
    const state = gameState.current;
    if (state.airJumpEnabled && state.score >= state.airJumpThreshold && state.score < state.airJumpThreshold + 50) {
        drawRetroText(ctx, 'Double Jump!', containerWidth / 2, 70, 16, '#fff');
    }
    if (scoreRef.current) scoreRef.current.textContent = `Score: ${state.score}`;
  };

  // Main Draw Function
  const draw = useCallback(() => {
    const canvas = canvasRef.current; const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Reset any lingering global effects like shadow before drawing anything
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;

    drawBackgroundAndClouds(ctx);
    if (gameState.current.gameStarted || gameState.current.gameOver) {
        drawGamePlatforms(ctx);
        drawPlayerUnicorn(ctx); // Handles its own potential glow effect internally & resets
        drawUI(ctx); // Handles its own potential text shadow internally & resets
    }
  }, [containerWidth, containerHeight, drawRetroText]); // Dependencies updated

  // Game Loop
  const gameLoop = useCallback(() => {
    update();
    draw();
    requestRef.current = requestAnimationFrame(gameLoop);
  }, [update, draw]);

  // Update Start/Game Over Message Text - Defined outside useEffect to be callable
   const updateStartMessage = useCallback(() => {
        if (!startMessageRef.current) return;
        const { gameOver, score, isTouchDevice } = gameState.current;
        const msgSpan = startMessageRef.current.children[0] as HTMLElement;
        const hintSpan = startMessageRef.current.children[1] as HTMLElement;

        if (gameOver) {
            msgSpan.textContent = `Game Over! Score: ${score}`;
            hintSpan.innerHTML = isTouchDevice ? '<br><span class="hint-text">Tap to Restart</span>' : '<br><span class="hint-text">Press SPACE to Restart</span>';
        } else {
            msgSpan.textContent = "Tap / Press SPACE to Start";
            hintSpan.innerHTML = isTouchDevice ? '<br><span class="hint-text">Tap Left/Right to Move, Upper Tap to Jump</span>' : '<br><span class="hint-text">Use Arrow Keys & Space</span>';
        }
  }, []); // No dependencies, it reads directly from gameState.current


  // Initialization and Event Listeners Setup
  useEffect(() => {
    if (!assetsLoaded) return;

    const canvas = canvasRef.current; if (!canvas) return;
    canvas.width = containerWidth; canvas.height = containerHeight;
    gameState.current.minUnicornY = containerHeight / 3.5;
    gameState.current.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    resetGame();

    const handleKeyDown = (e: KeyboardEvent) => {
        const { gameStarted, gameOver } = gameState.current; const key = e.key;
        if (!gameStarted && !gameOver && (key === " " || key === "Spacebar")) startGame();
        else if (gameOver && (key === " " || key === "Spacebar")) resetGame();
        else if (gameStarted && !gameOver) {
            if (key === "ArrowLeft") gameState.current.unicorn.targetDx = -gameState.current.unicorn.maxDx;
            else if (key === "ArrowRight") gameState.current.unicorn.targetDx = gameState.current.unicorn.maxDx;
            else if (key === " " || key === "Spacebar") jump();
        }
        if (key === "Escape") onExit();
    };
    const handleKeyUp = (e: KeyboardEvent) => {
        const key = e.key;
        if (key === "ArrowLeft" && gameState.current.unicorn.targetDx < 0) gameState.current.unicorn.targetDx = 0;
        else if (key === "ArrowRight" && gameState.current.unicorn.targetDx > 0) gameState.current.unicorn.targetDx = 0;
    };
    const handleTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        const { gameStarted, gameOver } = gameState.current;
        if (!gameStarted && !gameOver) { startGame(); return; }
        if (gameOver) { resetGame(); return; }
        if (gameStarted && !gameOver) {
            const touchX = e.touches[0].clientX - canvas.getBoundingClientRect().left;
            const touchY = e.touches[0].clientY - canvas.getBoundingClientRect().top;
            if (touchY < canvas.height * 0.6) jump(); // Upper taps jump
            if (touchY > canvas.height * 0.3) { // Lower taps move
                if (touchX < canvas.width / 2) gameState.current.unicorn.targetDx = -gameState.current.unicorn.maxDx;
                else gameState.current.unicorn.targetDx = gameState.current.unicorn.maxDx;
            }
        }
    };
    const handleTouchEnd = (e: TouchEvent) => {
        e.preventDefault();
        if (gameState.current.unicorn.targetDx !== 0) gameState.current.unicorn.targetDx = 0;
    };

    document.addEventListener("keydown", handleKeyDown); document.addEventListener("keyup", handleKeyUp);
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", handleTouchEnd, { passive: false });

    requestRef.current = requestAnimationFrame(gameLoop);

    return () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        document.removeEventListener("keydown", handleKeyDown); document.removeEventListener("keyup", handleKeyUp);
        canvas.removeEventListener("touchstart", handleTouchStart); canvas.removeEventListener("touchend", handleTouchEnd);
        canvas.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [assetsLoaded, containerWidth, containerHeight, resetGame, startGame, jump, gameLoop, onExit, updateStartMessage]);


  return (
    <div
      className="w-full h-full relative flex justify-center items-center bg-black/90 overflow-hidden"
      style={{ touchAction: 'none', cursor: 'pointer' }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
        .game-ui-text { font-family: 'Press Start 2P', monospace; }
        .hint-text { font-size: 0.8em; color: #ccc; }
      `}} />
      <canvas ref={canvasRef} className="block rounded-lg" style={{ touchAction: 'none', WebkitTapHighlightColor: 'transparent' }} />
      <div ref={titleRef} className="game-ui-text absolute top-12 left-1/2 transform -translate-x-1/2 text-3xl md:text-4xl text-white z-10 pointer-events-none" style={{ textShadow: '3px 3px 0px black', letterSpacing: '2px' }}>
        STACK JUMPER
      </div>
      <div ref={scoreRef} className="game-ui-text absolute top-4 left-1/2 transform -translate-x-1/2 text-xl md:text-2xl text-white z-10 pointer-events-none" style={{ textShadow: '2px 2px 0px black', fontSize: '16px' }}>
        Score: 0
      </div>
      {/* Ensure spans exist for updateStartMessage */}
      <div ref={startMessageRef} className="game-ui-text absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-base md:text-xl text-white text-center bg-black/60 p-4 rounded-lg z-10 max-w-[90%] pointer-events-none" style={{ fontSize: '14px', lineHeight: '1.6' }}>
        <span></span><span className="controls-hint inline-block"></span>
      </div>
      <button title="Exit Game (Esc)" className="absolute top-2 right-2 text-white bg-gray-700 hover:bg-gray-600 p-2 rounded-full w-8 h-8 flex items-center justify-center z-20" onClick={onExit}>X</button>
    </div>
  );
};

export default StackJumper;